import React, { useCallback, useEffect, useState } from 'react';
import {
  $saveTemplateVersionBody,
  SaveTemplateVersionBody,
} from '@loyalty/shared-common/template-api-client';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DialogClose } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../shared/layout/Title';
import { useEditorStore } from '../../store';

export interface JsonEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function JsonEditorModal({ isOpen, onClose }: JsonEditorModalProps): React.ReactElement {
  const { t } = useTranslation();

  // Zustand store
  const markAsDirty = useEditorStore(state => state.markAsDirty);
  const setEditorState = useEditorStore(state => state.setEditorState);
  const getTemplateJSON = useEditorStore(state => state.getTemplateJSON);

  const [jsonContent, setJsonContent] = useState<string>('');
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize JSON content when modal opens
  useEffect(() => {
    if (isOpen) {
      const json = getTemplateJSON();
      setJsonContent(JSON.stringify(json, null, 2));
      setIsDirty(false);
      setError(null);
    }
  }, [isOpen, getTemplateJSON]);

  const handleChange = useCallback((value: string | undefined) => {
    setJsonContent(value || '');
    setIsDirty(true);
    setError(null);
  }, []);

  const handleApply = useCallback(() => {
    try {
      // Parse JSON
      const parsed: SaveTemplateVersionBody = JSON.parse(jsonContent);

      // Validate against schema
      const validationResult = $saveTemplateVersionBody.validate(parsed);
      if (validationResult.error) {
        setError(
          t('editor.json-editor.errors.validation', {
            message: validationResult.error.message,
            defaultValue: 'Invalid template structure: {{message}}',
          }),
        );
        return;
      }

      // Apply changes
      setEditorState(parsed);
      markAsDirty();
      onClose();
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setError(
        t('editor.json-editor.errors.parse', {
          message: errorMessage,
          defaultValue: 'Invalid JSON: {{message}}',
        }),
      );
    }
  }, [jsonContent, onClose, t, setEditorState, markAsDirty]);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogClose close={handleClose} />
      <DialogTitle>
        <Title>{t('editor.json-editor.title', 'Template JSON Editor')}</Title>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}
        <QlfCodeArea
          id="json-editor"
          value={jsonContent}
          defaultLanguage="json"
          allowFullScreen
          height="70vh"
          onChange={handleChange}
        />
      </DialogContent>
      <DialogActions sx={{ paddingX: '40px', justifyContent: 'space-between !important' }}>
        <Button color="grey" onClick={handleClose} startIcon={<FontAwesomeIcon icon={faXmark} />}>
          {t('generic.actions.cancel')}
        </Button>
        <Button
          onClick={handleApply}
          startIcon={<FontAwesomeIcon icon={faCheck} />}
          disabled={!isDirty}
          autoFocus
        >
          {t('generic.actions.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
